import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/auth.store";
import { AllConsultantsData, fetchConsultantSyncVersion } from "./consultant.hooks";
import { AllCustomersData, fetchCustomerSyncVersion } from "./customer.hooks";

/**
 * Cheap revalidation poll for the persisted full consultant/customer lists.
 *
 * The lists (useAllConsultants/useAllCustomers) are cached with
 * staleTime: Infinity and persisted to IndexedDB, so they never refetch on
 * their own. This watcher polls the two tiny sync-version endpoints and
 * invalidates exactly the cached list entries whose stored `syncVersion` no
 * longer matches the server's — i.e. the expensive multi-thousand-row paging
 * only ever happens when server data actually changed. Per-pool variants each
 * carry the version they were fetched at, so the pool dimension is handled
 * per cache entry.
 *
 * Mounted once in LoggedInLayout, which only renders for authenticated users;
 * the query is additionally gated on isLoggedIn for safety.
 */
export function useSyncVersionWatcher() {
	const queryClient = useQueryClient();
	const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

	useQuery(
		["sync-versions"],
		async () => {
			const [consultants, customers] = await Promise.all([
				fetchConsultantSyncVersion(),
				fetchCustomerSyncVersion(),
			]);

			return { consultants, customers };
		},
		{
			enabled: isLoggedIn,
			refetchInterval: 60_000,
			refetchIntervalInBackground: false,
			refetchOnWindowFocus: true,
			staleTime: 0,
			onSuccess: ({ consultants, customers }) => {
				queryClient
					.getQueriesData<AllConsultantsData>(["consultants", "all"])
					.forEach(([queryKey, data]) => {
						if (data && data.syncVersion !== consultants) {
							queryClient.invalidateQueries({ queryKey, exact: true });
						}
					});

				queryClient
					.getQueriesData<AllCustomersData>(["customers", "all"])
					.forEach(([queryKey, data]) => {
						if (data && data.syncVersion !== customers) {
							queryClient.invalidateQueries({ queryKey, exact: true });
						}
					});
			},
		}
	);
}
